import { ReactElement, useEffect, useState } from 'react';
import { AutocompleteRenderInputParams, PaginatedSearchDropdown, Text } from '@hopskipdrive/react-components';

import { colorTokens } from '../../../shared/components/variables';
import { IncidentIndividual } from '../../types/incidents';

import { AssigneeItem, AssigneeTextField, StyledAssigneeAvatar } from './AssigneeSelect.styles';

const combineFirstAndLastName = (assignee: IncidentIndividual | null) => {
  return assignee ? assignee.firstName + ' ' + assignee.lastName : '';
};

export interface AssigneeSelectProps {
  options: IncidentIndividual[];
  value: IncidentIndividual | null;
  onChange: (value: IncidentIndividual | null) => void;
  loadNextPage: () => void;
  search: (input: string) => void;
  isLoading: boolean;
  page: number;
  perPage: number;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
}

export const AssigneeAvatar = ({ assignee }): ReactElement => {
  return (
    <StyledAssigneeAvatar
      alt={combineFirstAndLastName(assignee)}
      style={{ backgroundColor: colorTokens.avatar[assignee?.id ? assignee.id % colorTokens.avatar.length : 0] }}
    >
      <Text variant="subtitle2">
        {assignee && assignee.firstName && assignee.lastName
          ? (assignee.firstName[0] + assignee.lastName[0]).toUpperCase()
          : ''}
      </Text>
    </StyledAssigneeAvatar>
  );
};

const AssigneeSelect = ({
  options,
  value,
  onChange,
  loadNextPage,
  search,
  isLoading,
  page,
  perPage,
  error,
  helperText,
  placeholder,
  ...rest
}: AssigneeSelectProps): ReactElement => {
  const [inputValue, setInputValue] = useState<string>(combineFirstAndLastName(value) || '');
  const filterOptionsOnSearch = (options) => {
    if (inputValue) {
      const lowercasedSearchString = inputValue.toLowerCase();

      return options.filter((option) => {
        return (
          option.label?.toLowerCase().includes(lowercasedSearchString) || option.slug?.includes(lowercasedSearchString)
        );
      });
    }

    return options;
  };

  useEffect(() => {
    search(inputValue);
  }, [inputValue]);

  return (
    <PaginatedSearchDropdown
      perPage={perPage}
      pageNumber={page}
      isLoading={isLoading}
      loadNextPage={loadNextPage}
      inputValue={inputValue}
      setInputValue={setInputValue}
      options={options.map((option) => ({
        label: combineFirstAndLastName(option),
        slug: option?.slug || '',
      }))}
      disableClearable={value ? undefined : true}
      filterOptions={filterOptionsOnSearch}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      value={value ? { label: combineFirstAndLastName(value), slug: value?.slug || '' } : null}
      onChange={(_, value) => {
        onChange(options.find((option) => option?.slug === value?.slug) || null);
      }}
      handleSelected={onChange}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <AssigneeTextField
          {...params}
          className={value ? 'selected' : ''}
          variant="outlined"
          placeholder={placeholder}
          error={error}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && value) {
              onChange(null);
            }
          }}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {params.InputProps.startAdornment}
                {value ? <AssigneeAvatar assignee={value} /> : null}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <div key={option.label}>
            <AssigneeItem {...props} key={option.label}>
              <AssigneeAvatar assignee={options.find((item) => item?.slug === option?.slug)} />
              <Text variant="body1">{option.label}</Text>
            </AssigneeItem>
          </div>
        );
      }}
      {...rest}
    />
  );
};

export default AssigneeSelect;
